import {ChartDataset} from 'chart.js';

import {ApiOrganizationAreaUnit} from 'app/modules/Remote/Organization/types';

import {DataRange} from '../AnalyzePolygonChart/types';

export type FeatureStatProperties = {
  multiFeaturePartName: string | null;
  name: string;
  projectId: string;
  tags: string[];
};

type ApiResponseEntry<Data> = Data | {error: string};

export type CategoricalPayload = {
  stats: {
    [date: string]:
      | {
          [categoryKey: string]: number | null | undefined;
        }
      | null
      | undefined;
  };
  properties: FeatureStatProperties;
};
export type MFAPayload = {
  stats: {
    [date: string]: number | null | undefined;
  };
  properties: FeatureStatProperties;
};

export type ChartDatasetPoints = {[date: string]: number | null};

export type CategoricalStatResponse = Record<string, ApiResponseEntry<CategoricalPayload>>;

export type MFAResponse = Record<string, ApiResponseEntry<MFAPayload>>;

export type FeatureStatsApiResponse = CategoricalStatResponse | MFAResponse;

export type ReportsQueryParams = {
  startDate?: string;
  endDate?: string;
};

export type NotesQueryParams = {
  startDate?: string;
  endDate?: string;
  noteType?: string;
};

export type FeatureMetadataApiResponse = {
  [featureId: number]: {
    name: string;
    multiFeaturePartName: string;
    areaAcres: number;
    projectId: string;
    projectName: string;
    tags: string[];
    totalCount: number;
  };
};

export type OrgNoteStatsApiResponse = {
  [noteId: number]: {
    author: string | null;
    createdAt: string;
    projectName: string;
    projectId: string;
    tags: string[] | null;
    totalCount: number;
    featureId: number;
    shortText: string;
  };
};

export type OrgReportStatsApiResponse = {
  [reportId: number]: {
    createdAt: string;
    projectName: string;
    projectId: string;
    author: string;
    totalCount: number;
  };
};

export interface BarChartCategoryData extends ChartDataset<'bar', number[]> {
  id: string;
  label: string;
  backgroundColor?: string;
  category?: string;
  properties?: FeatureStatProperties;
}
export interface BaseWidgetConfig {
  id: number;
}

export interface ChartConfig extends BaseWidgetConfig {
  title: string;
  description?: string;
  type: 'chart';
  isPublished: boolean;
}

export const isMultifeatureAnalysisConfig = (
  config: WidgetConfig
): config is MultifeatureAnalysisConfig => {
  return 'statAggregation' in config;
};

export type TrendLineAggregation = 'mean' | 'sum';

export type WidgetDisplayOptions = {
  areaUnit?: ApiOrganizationAreaUnit;
  title?: string;
  description?: string;
  categoricalKey?: string;
  byArea?: boolean;
  /**
   * Restrict a categorical widget to this subset of category keys (e.g. ['3', '4', '5'] to show
   * only the Moderate/High/Severe ISI classes). Undefined/empty shows every category in the data.
   */
  includedCategories?: string[];
  dataRange?: DataRange;
  /**
   * `trendlineAggregation` is an optional prop that can override the default trendline we draw for
   * certain chart types based on the statType.
   *
   * Defaults:
   * - For the 'mean' statType, we show the average of all features at a timestep as the trendline
   * - For the 'sum' statType, we show the sum of all features at a timestep as the trendline
   *
   * In some cases, we may want to override, ie for carbon stock, where we want the average carbon stock
   * across all features as the trendline. In this case, the statType would be 'sum' and the trendLineAggregation
   * would be 'mean'.
   */
  trendLineAggregation?: TrendLineAggregation;
  chartHidden?: boolean;
};

export interface MultifeatureAnalysisConfig extends BaseWidgetConfig {
  startDate: string | null;
  endDate: string | null;
  /**
   * The statType refers to the specific stat bucket we are pulling from for
   * the scene stats. Think of these as, for the pixels in each scene, how
   * are they being combined? Examples:
   *
   * For vegetation - we usually want averages for each pixel in the scene,
   * so we provide 'mean' as the statType.
   *
   * For carbon stock - we want to know the amount of carbon at each pixel, so for
   * each scene we add all the "stock" in each pixel together and request stats
   * from the 'sum' statType.
   *
   * For carbon flux - we want the total flux (MgC/yr) across all pixels by computing
   * sum(value x pixel_area). This is pre-computed by the backend and stored in
   * _aggregate_statistics. Use 'aggregate_sum' to access this data.
   */
  statAggregation: 'mean' | 'sum' | 'area_counts' | 'area_percentages' | 'aggregate_sum';
  layerKey: string;
  sourceId: string;
  timeAggregation: 'monthly' | 'yearly';
  title: string;
  description: string;
  isPublished: boolean;
  // If not provided, we will use all features for the organization.
  featureIds?: string[] | null;
  displayOptions?: WidgetDisplayOptions;
}
export type WidgetConfig = MultifeatureAnalysisConfig | ChartConfig;
export type DashboardConfig = {widgets: WidgetConfig[]};
